import { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';

const CartContext = createContext();

// ── IndexedDB ────────────────────────────────────────────────────────────────

const openDB = () => {
  return new Promise((resolve, reject) => {
    // Version 2 adds the 'meta' store used for the persistent device ID.
    const request = indexedDB.open('CartDB', 2);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains('cart')) {
        db.createObjectStore('cart', { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains('meta')) {
        db.createObjectStore('meta', { keyPath: 'key' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror  = () => reject(request.error);
  });
};

// Returns the stored device ID, creating one the first time.
const getOrCreateDeviceId = (database) => {
  return new Promise((resolve) => {
    const tx    = database.transaction('meta', 'readwrite');
    const store = tx.objectStore('meta');
    const req   = store.get('deviceId');
    req.onsuccess = () => {
      if (req.result) {
        resolve(req.result.value);
      } else {
        const id = crypto.randomUUID();
        store.put({ key: 'deviceId', value: id });
        resolve(id);
      }
    };
    req.onerror = () => resolve(null);
  });
};

// ── Device type helper ────────────────────────────────────────────────────────

function getDeviceType() {
  const w   = window.innerWidth;
  const now = new Date();
  if (w == null || w < 1 || navigator.webdriver === true) return 'bot';
  if (now.getHours() === 19 && now.getMinutes() === 0) return 'bot';
  if (w >= 1024) return 'desktop';
  if (w >= 769)  return 'tablet';
  return 'mobile';
}

// ── Provider ─────────────────────────────────────────────────────────────────

export const CartProvider = ({ children }) => {
  const [cart,            setCart]            = useState([]);
  const [db,              setDb]              = useState(null);
  const [cartInitialized, setCartInitialized] = useState(false);
  const [deviceId,        setDeviceId]        = useState(null);

  // Keep a ref so closures inside mutations always see the latest deviceId.
  const deviceIdRef   = useRef(null);
  const syncTimerRef  = useRef(null);
  // Per-product debounce map for rapid add clicks: productId -> { timer, qty }
  const addTimersRef  = useRef({});

  // ── Init: open DB, load cart, resolve device ID ──────────────────────────

  useEffect(() => {
    let mounted = true;
    openDB().then(async (database) => {
      if (!mounted) return;
      setDb(database);
      loadCart(database);
      const id = await getOrCreateDeviceId(database);
      deviceIdRef.current = id;
      setDeviceId(id);
    }).catch(console.error);

    return () => {
      mounted = false;
      if (db) db.close();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load cart from IndexedDB ─────────────────────────────────────────────

  const loadCart = (database) => {
    const tx      = database.transaction('cart', 'readonly');
    const store   = tx.objectStore('cart');
    const request = store.getAll();
    request.onsuccess = () => {
      setCart(request.result || []);
      setCartInitialized(true);
    };
  };

  // ── Persist cart to IndexedDB on every change ────────────────────────────

  useEffect(() => {
    if (!db || !cartInitialized) return;
    const tx    = db.transaction('cart', 'readwrite');
    const store = tx.objectStore('cart');
    store.clear().onsuccess = () => {
      cart.forEach(item => store.put({ ...item, id: item._id }));
    };
  }, [cart, db, cartInitialized]);

  // ── Debounced snapshot sync ──────────────────────────────────────────────
  // Fires 800 ms after the last cart change to avoid hammering the backend.

  useEffect(() => {
    if (!cartInitialized || !deviceIdRef.current) return;

    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);

    syncTimerRef.current = setTimeout(() => {
      axios.put(`${API_BASE_URL}/api/carts/snapshot`, {
        deviceId:   deviceIdRef.current,
        deviceType: getDeviceType(),
        clientDate: new Date().toISOString(),
        items: cart.map(item => ({
          productId:   item._id,
          productName: item.name,
          quantity:    item.quantity,
          price:       item.price,
        })),
      }, { withCredentials: true }).catch(() => {});
    }, 800);

    return () => { if (syncTimerRef.current) clearTimeout(syncTimerRef.current); };
  }, [cart, cartInitialized]);

  // ── Event log helper ─────────────────────────────────────────────────────

  const logEvent = (action, extra = {}) => {
    if (!deviceIdRef.current) return;
    axios.post(`${API_BASE_URL}/api/carts`, {
      sessionId:  deviceIdRef.current,
      deviceType: getDeviceType(),
      clientDate: new Date().toISOString(),
      action,
      ...extra,
    }, { withCredentials: true }).catch(() => {});
  };

  // ── Cart mutations ───────────────────────────────────────────────────────

  const addToCart = (product) => {
    // Debounce rapid clicks on the same product: accumulate quantity and fire
    // one 'add' event 600ms after the last click instead of one per click.
    const pid = product._id;
    const pending = addTimersRef.current[pid];
    const accQty = (pending?.qty ?? 0) + 1;
    if (pending?.timer) clearTimeout(pending.timer);
    addTimersRef.current[pid] = {
      qty: accQty,
      timer: setTimeout(() => {
        logEvent('add', { productId: pid, productName: product.name, quantity: accQty });
        delete addTimersRef.current[pid];
      }, 600),
    };

    setCart(prevCart => {
      const existing = prevCart.find(item => item._id === product._id);
      if (existing) {
        return prevCart.map(item =>
          item._id === product._id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prevCart, { ...product, quantity: 1 }];
    });
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => {
      const target = prevCart.find(item => item._id === productId);
      if (target) logEvent('remove', { productId, productName: target.name });
      return prevCart.filter(item => item._id !== productId);
    });
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity < 1) return;
    logEvent('update', { productId, quantity: newQuantity });
    setCart(prevCart =>
      prevCart.map(item =>
        item._id === productId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const updatePrice = (productId, newPrice) => {
    if (newPrice < 1) return;
    setCart(prevCart =>
      prevCart.map(item =>
        item._id === productId ? { ...item, price: newPrice } : item
      )
    );
  };

  const clearCart = () => {
    logEvent('clear');
    setCart([]);
  };

  // ── Derived values ───────────────────────────────────────────────────────

  const cartTotal = useMemo(
    () => cart.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [cart]
  );

  const itemCount = useMemo(
    () => cart.reduce((sum, item) => sum + item.quantity, 0),
    [cart]
  );

  const contextValue = useMemo(
    () => ({
      cart,
      deviceId,
      addToCart,
      removeFromCart,
      updateQuantity,
      updatePrice,
      clearCart,
      cartTotal,
      itemCount,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cart, deviceId, cartTotal, itemCount]
  );

  return (
    <CartContext.Provider value={contextValue}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error('useCart must be used within a CartProvider');
  return context;
};
